<template>
	<div id="asset-element-container">
		<div
			v-if="showHeaderSection"
			class="module-header display-flex align-items-center justify-content-space-between"
		>
			<h2 v-if="props.showAssetsHeading" class="heading-5">Assets</h2>
			<div v-if="showStatusFilter || !hideViewOptions" class="display-flex align-items-center gap-200">
				<SegmentedControl
					v-if="showStatusFilter"
					:controls="statusOptions"
					:selected="selectedStatusKey"
					@selected-segmented-control-key="changeStatus"
				/>
				<SegmentedControl
					v-if="!hideViewOptions"
					:controls="viewOptions"
					:selected="selectedViewKey"
					@selected-segmented-control-key="changeView"
				/>
			</div>
		</div>
		<div class="module-section">
			<div v-if="props.assetElements && props.assetElements.length > 0">
				<transition name="component-fade" mode="out-in">
					<div v-if="filteredAssetsCount">
						<BaseAlert
							v-if="showNoAddressAssetsMessage"
							:alert="{
								message: ['Assets without a physical address are not included in this view.'],
								type: 'info'
							}"
							class="mt-100 mt-200-md mb-100"
							data-test="no-address-assets-message"
						/>
						<div v-if="showFilters" data-test="asset-element-filter">
							<div
								v-if="!showFilter"
								key="hideFilter"
								class="display-flex align-items-center justify-content-space-between mb-100 pb-100 bb"
							>
								<BaseButton
									classes="button button-outline asset-filter-button"
									data-test="asset-filter-open"
									@click="toggleFilter()"
								>
									<span v-if="filterCount > 0" class="display-flex align-items-center gap-50">
										<span
											class="badge badge-text filter-badge bg-color-rust text-color-white pl-2"
											data-test="asset-filter-badge"
											>{{ filterCount }}</span
										><span>Filter</span>
									</span>
									<span v-else class="display-flex align-items-center gap-50">
										<BaseSvgIcon name="filter" width="20" height="20" /><span>Filter</span>
									</span>
								</BaseButton>
								<div class="body-lg font-weight-bold" v-html="viewAssetsText"></div>
							</div>
							<div v-else key="showFilter">
								<div class="display-flex flex-direction-column pb-100 bb">
									<div
										class="display-flex align-items-center justify-content-space-between mb-100 pb-100"
									>
										<BaseButton
											classes="display-flex align-items-center gap-50 text-color-content-primary close-filter-btn"
											data-test="asset-filter-close"
											@click="toggleFilter()"
										>
											<BaseSvgIcon name="close" width="20" height="20" /><span>Filter</span>
										</BaseButton>
										<div class="body-lg font-weight-bold" v-html="viewAssetsText"></div>
									</div>
									<div class="filter-container">
										<div
											v-for="filter in props.filterOptions"
											:key="filter.filterClassLabel"
											class="mb-150-smo filter-element"
										>
											<div class="overline mb-100">{{ filter.filterClassLabel }}</div>
											<div class="filter-container-items">
												<div
													v-for="(filterType, idx) in filter.filterType"
													:key="idx"
													class="radio-checkbox-item"
												>
													<label :for="`${filterType}-${idx}`">
														<input
															:id="`${filterType}-${idx}`"
															v-model="selectedFilters"
															type="checkbox"
															:data-test="`asset-filter-${idx}`"
															:name="filterType"
															:value="filterType"
															@change="addFilter(filter.filterClass, filterType, $event)"
														/>
														<span>{{ filterType }}</span>
														<span class="mask"></span>
													</label>
												</div>
											</div>
										</div>
									</div>
									<BaseButtonGroup>
										<template #secondary>
											<BaseButton classes="button button-ghost" @click="clearFilters()"
												>Clear filter</BaseButton
											>
										</template>
										<template #primary>
											<BaseButton
												classes="button button-default"
												size="mini"
												@click="applyFilters()"
												>Apply filter</BaseButton
											>
										</template>
									</BaseButtonGroup>
								</div>
							</div>
						</div>
						<div v-else class="body-lg font-weight-bold text-align-right" v-html="viewAssetsText"></div>
					</div>
				</transition>
				<div class="mt-100">
					<div v-if="filteredAssetsCount">
						<transition name="component-fade" mode="out-in">
							<keep-alive>
								<component
									:is="currentView"
									:asset-elements="filteredAssetElements"
									:asset-elements-per-page="props.assetElementsPerPage"
									:use-large-cards="props.useLargeCards"
									@paginated="onPagination"
								/>
							</keep-alive>
						</transition>
					</div>
					<div v-else>
						<div v-if="filterCount">
							<BaseButton classes="text-color-rust" @click="clearFilters()">Reset all filters</BaseButton>
							or remove one of your filters to see more assets.
						</div>
						<div v-else>No assets to display</div>
					</div>
				</div>
				<div v-if="props.showDisclaimerIfApplicable" class="body-xs text-color-content-tertiary mt-300">
					*The projected annual return presented for this project is at the portfolio level, because it is
					held in a pooled portfolio.
				</div>
			</div>
			<div v-else>
				<AccountModulePlaceholder
					title="Asset details not available"
					message="Asset details will become available as soon as your portfolio acquires its first asset."
					data-test="no-asset-elements-placeholder"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { AssetContainerViewType, AssetFilterOptions, FilterEvent } from 'types/asset-details';
import { computed, Ref, ref, watch } from 'vue';
import {
	filterByAssetIndustry,
	filterByAssetType,
	filterByOfferingName,
	filterByStrategy
} from '@utils/performance-portfolio';
import AccountModulePlaceholder from '@components/account/account-module-placeholder.vue';
import { Asset } from 'types/assets';
import AssetElementCardGrid from '@components/asset-element/asset-element-card-grid.vue';
import AssetElementCardList from '@components/asset-element/asset-element-card-list.vue';
import AssetElementMap from '@components/asset-element/asset-element-map.vue';
import { pluralize } from '@filters/shared-filters';
import { scrollToAnchor } from '@utils/scroll-to';
import SegmentedControl from '@components/segmented-control/segmented-control.vue';
import { SegmentedControlButton } from 'types/layout';
import { useAppStore } from '@stores/app';
import type { Component as ComponentType } from 'vue';

type AssetStatusType = 'Active' | 'Completed';

interface Props {
	assetElements: Array<Asset>;
	assetElementsPerPage?: number;
	customFilter?: FilterEvent;
	defaultToMapView?: boolean;
	filterOptions?: Array<AssetFilterOptions>;
	showAssetsHeading?: boolean;
	showDisclaimerIfApplicable?: boolean;
	useGrid?: boolean;
	useIconViewOptions?: boolean;
	useLargeCards?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	assetElementsPerPage: 8,
	customFilter: undefined,
	defaultToMapView: false,
	filterOptions: () => [],
	showAssetsHeading: true,
	showDisclaimerIfApplicable: true,
	useGrid: false,
	useIconViewOptions: false,
	useLargeCards: false
});

const appStore = useAppStore();

const selectedFiltersMap: Ref<Record<string, Array<string>>> = ref({});
const selectedFilters: Ref<Array<string>> = ref([]);
const filterApplied = ref(false);
const selectedStatus: Ref<AssetStatusType> = ref('Active');
const showFilter = ref(false);

const currentView = computed((): ComponentType => {
	if (selectedView.value === 'Map') {
		return AssetElementMap;
	} else {
		return props.useGrid ? AssetElementCardGrid : AssetElementCardList;
	}
});

const defaultContainerView = computed((): AssetContainerViewType => {
	if (props.defaultToMapView) {
		return 'Map';
	} else {
		return props.useGrid ? 'Grid' : 'List';
	}
});

const selectedStatusKey = computed((): string => {
	return (
		statusOptions.value.find((control) => control.key === selectedStatus.value)?.key ?? statusOptions.value[0].key
	);
});

const selectedViewKey = computed((): string => {
	return viewOptions.value.find((ctrl) => ctrl.label === selectedView.value)?.key ?? viewOptions.value[0].key;
});

const filteredAssetsCount = computed((): number => filteredAssetElements.value.length);

const filterCount = computed((): number => selectedFilters.value.length);

const hasAssetsWithAddresses = computed((): boolean =>
	filteredAssetElements.value.some((asset) => !!asset.realEstateAndPrivateCreditFields?.streetAddress)
);

const hideViewOptions = computed((): boolean => viewOptions.value.length <= 1);

const showFilters = computed((): boolean => props.filterOptions.length > 0);

const showStatusFilter = computed(
	(): boolean =>
		!showFilters.value &&
		props.assetElements.some((a) => a.status.name === 'ACTIVE') &&
		props.assetElements.some((a) => a.status.name === 'INACTIVE')
);

const showNoAddressAssetsMessage = computed(
	(): boolean => viewableAssetElements.value.length !== props.assetElements.length
);

const viewOptions = computed((): Array<SegmentedControlButton> => {
	const controls: Array<SegmentedControlButton> = [
		{
			isIcon: props.useIconViewOptions,
			key: props.useGrid ? 'grid' : 'list',
			label: props.useGrid ? 'Grid' : 'List'
		}
	];

	if (hasAssetsWithAddresses.value && !appStore.isMobile) {
		controls.push({
			isIcon: props.useIconViewOptions,
			key: 'map-pin',
			label: 'Map'
		});
	}

	return controls;
});

const selectedView: Ref<AssetContainerViewType> = ref(defaultContainerView.value || viewOptions.value[0].label);

const viewableAssetElements = computed(
	(): Array<Asset> =>
		selectedView.value === 'Map'
			? props.assetElements.filter(
					(asset) =>
						!!asset.realEstateAndPrivateCreditFields?.latitude &&
						!!asset.realEstateAndPrivateCreditFields?.longitude
				)
			: props.assetElements
);

const filteredAssetElements: Ref<Array<Asset>> = ref(viewableAssetElements.value);

const viewAssetsText = computed((): string => {
	const assetsCount = viewableAssetElements.value.length;
	const assetText = pluralize(assetsCount, 'asset');
	return filterApplied.value || showStatusFilter.value
		? `Viewing <span class="text-color-rust">${filteredAssetsCount.value}</span> of ${assetsCount} ${assetText}`
		: `Viewing ${assetsCount} ${assetText}`;
});

const statusOptions = computed((): Array<SegmentedControlButton> => {
	if (showStatusFilter.value) {
		return [
			{
				key: 'Active',
				label: 'Active'
			},
			{
				key: 'Completed',
				label: 'Completed'
			}
		];
	}

	return [];
});

const showHeaderSection = computed((): boolean => {
	return props.showAssetsHeading || showStatusFilter.value || !hideViewOptions.value;
});

watch(
	() => props.customFilter,
	() => {
		if (props.customFilter) {
			clearFilters();
			updateSelectedFilterMap(props.customFilter);
			updateFilteredAssetElements();
		}
	},
	{ immediate: true }
);

watch(
	() => showStatusFilter.value,
	() => {
		if (showStatusFilter.value) updateFilteredAssetElements();
	},
	{ immediate: true }
);

watch(
	() => viewOptions.value,
	() => {
		if (!viewOptions.value.find((o) => o.label === selectedView.value)) {
			selectedView.value = defaultContainerView.value;
		}
	}
);

function updateFilteredAssetElements(): void {
	filteredAssetElements.value = [];

	if (showStatusFilter.value) {
		for (const element of viewableAssetElements.value) {
			if (element.status.label === selectedStatus.value) filteredAssetElements.value.push(element);
		}
	} else {
		for (const element of viewableAssetElements.value) {
			if (
				filterByOfferingName(element, selectedFiltersMap.value) &&
				filterByAssetType(element, selectedFiltersMap.value) &&
				filterByStrategy(element, selectedFiltersMap.value) &&
				filterByAssetIndustry(element, selectedFiltersMap.value)
			) {
				filteredAssetElements.value.push(element);
			}
		}

		filterApplied.value = filterCount.value > 0;
	}

	if (selectedView.value === 'Map' && !hasAssetsWithAddresses.value) {
		changeView(viewOptions.value[0].key);
	}
}

function updateSelectedFilterMap(filterEvent: FilterEvent): void {
	const { filterClass, filterType, checked } = filterEvent;

	if (
		selectedFiltersMap.value[filterClass] &&
		selectedFiltersMap.value[filterClass].indexOf(filterType) !== -1 &&
		checked
	)
		return;
	if (selectedFiltersMap.value[filterClass]) {
		if (!checked) {
			const itemToRemoveIdx = selectedFiltersMap.value[filterClass].indexOf(filterType);
			selectedFiltersMap.value[filterClass].splice(itemToRemoveIdx, 1);
			if (selectedFiltersMap.value[filterClass].length === 0) {
				delete selectedFiltersMap.value[filterClass];
			}
		} else {
			selectedFiltersMap.value[filterClass]?.push(filterType);
		}
	} else {
		selectedFiltersMap.value[filterClass] = [filterType];
	}

	selectedFilters.value = Object.values(selectedFiltersMap.value).flat();
}

function changeStatus(newStatusKey: string): void {
	selectedStatus.value = statusOptions.value.find((option) => option.key === newStatusKey)?.key as AssetStatusType;
	updateFilteredAssetElements();
}

function changeView(selectedViewOptionKey: string): void {
	selectedView.value = viewOptions.value.find((option) => option.key === selectedViewOptionKey)
		?.label as AssetContainerViewType;
	updateFilteredAssetElements();
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function addFilter(filterClass: string, filterType: string, event: any): void {
	const filterObj: FilterEvent = {
		checked: event.target.checked,
		filterClass,
		filterType
	};
	updateSelectedFilterMap(filterObj);
}

function clearFilters(): void {
	selectedFilters.value = [];
	selectedFiltersMap.value = {};
	filteredAssetElements.value = viewableAssetElements.value;
	filterApplied.value = false;
}

function applyFilters(): void {
	updateFilteredAssetElements();
	toggleFilter();
}

function toggleFilter(): void {
	showFilter.value = !showFilter.value;
}

function onPagination(): void {
	scrollToAnchor('asset-element-container');
}
</script>
<style lang="scss" scoped>
@use 'sass:map';
@use '../../styles/utilities/respond-to.scss' as *;
@use '../../styles/constants/colors' as *;
.component-fade-enter-active,
.component-fade-leave-active {
	transition: opacity 0.3s ease;
}

.component-fade-enter,
.component-fade-leave-to {
	opacity: 0;
}

.filter-badge {
	width: 26px;
	height: 26px;
}
.asset-filter-button {
	padding: 0 1em;
	border-radius: 3px;
}
.pl-2 {
	padding-left: 2px;
}
.filter-container {
	display: flex;
	gap: 16px;
	flex-direction: column;

	@include respond-to(md) {
		flex-direction: row;
		flex-wrap: wrap;

		.filter-element {
			flex-basis: calc(50% - 16px);
		}
	}

	&-items {
		display: flex;
		column-gap: 16px;
		flex-wrap: wrap;

		.radio-checkbox-item {
			flex-basis: calc(50% - 16px);
			margin-top: 0;
			margin-bottom: 0.5rem;
		}
	}
}
.close-filter-btn {
	min-height: 3rem;

	&:hover {
		color: map.get($brand-colors, 'rust');
	}
}
</style>
